import { createSlice } from '@reduxjs/toolkit';
import { LOGOUT } from '../../../utils/constants';
import {
  addUserToCollectionBuilder,
  cancelInfluencerManualMassDmBuilder,
  createCollectionBuilder,
  editDisplayNameBuilder,
  fetchCollectionsBuilder,
  fetchCollectionUsersBuilder,
  fetchInfluencerChatBuilder,
  fetchInfluencerChatsBuilder,
  fetchInfluencerMassDmHistoryBuilder,
  fetchInfluencerNotificationStatusBuilder,
  fetchNotificationsBuilder,
  getAiChatSuggestionsBuilder,
  pinUserBuilder,
  sendMassDMMessageBuilder,
  sendMessageBuilder,
  toggleUserInfluencerManualChatBuilder,
  unblockUserInfluencerChatBuilder,
} from './builder';

const INIT_STATE = {
  fetchCollectionsInfo: {
    data: [],
    pending: false,
  },
  fetchCollectionUsersInfo: {
    data: [],
    maxReached: false,
    pending: false,
    fetchMorePending: false,
  },
  fetchInfluencerChatsInfo: {
    data: [],
    maxReached: false,
    pending: false,
    fetchMorePending: false,
    refreshing: false,
  },
  fetchInfluencerChatInfo: {
    data: [],
    hasMore: false,
    nextCursor: null,
    pending: false,
    fetchMorePending: false,
  },
  fetchNotificationsInfo: {
    pending: false,
    data: [],
    total: 0,
  },
  fetchInfluencerMassDmHistoryInfo: {
    data: [],
    maxReached: false,
    pending: false,
    fetchMorePending: false,
  },
  selectedCollectionId: null,
  hasNewNotifications: false,
  userInfluencer: null,
  activeUserInfluencerId: null,
  pending: {
    sendMessage: false,
    createCollection: false,
    pinUser: false,
    editDisplayName: false,
    addUserToCollection: false,
    sendMassDMMessage: false,
    toggleUserInfluencerManualChat: false,
    getAiChatSuggestions: false,
    unblockUserInfluencerChat: false,
    cancelInfluencerManualMassDm: false,
  },
};

export const collectionsSlice = createSlice({
  name: 'collections',
  initialState: INIT_STATE,
  reducers: {
    setHasNewNotifications: (state, { payload }) => {
      state.hasNewNotifications = payload;
    },
    setActiveUserInfluencerId: (state, { payload }) => {
      state.activeUserInfluencerId = payload;
    },
    setSelectedCollectionId: (state, { payload }) => {
      state.selectedCollectionId = payload;
    },
    setNewPurchase: (state, { payload }) => {
      state.fetchInfluencerChatsInfo.data = state.fetchInfluencerChatsInfo.data.map(chat => {
        if (chat.id === payload.chatId) {
          return { ...chat, newPurchase: payload.newPurchase };
        } else {
          return chat;
        }
      });
    },
    markChatAsReadLocal: (state, { payload }) => {
      const chat = state.fetchInfluencerChatsInfo.data.find(c => c.id === payload);
      if (chat) chat.numOfUnseenMessages = 0;
    },
  },
  extraReducers: builder => {
    fetchCollectionsBuilder(builder);
    createCollectionBuilder(builder);
    fetchCollectionUsersBuilder(builder);
    fetchInfluencerChatsBuilder(builder);
    fetchInfluencerChatBuilder(builder);
    sendMessageBuilder(builder);
    pinUserBuilder(builder);
    editDisplayNameBuilder(builder);
    addUserToCollectionBuilder(builder);
    sendMassDMMessageBuilder(builder);
    fetchNotificationsBuilder(builder);
    fetchInfluencerNotificationStatusBuilder(builder);
    toggleUserInfluencerManualChatBuilder(builder);
    getAiChatSuggestionsBuilder(builder);
    unblockUserInfluencerChatBuilder(builder);
    fetchInfluencerMassDmHistoryBuilder(builder);
    cancelInfluencerManualMassDmBuilder(builder);
    builder.addCase(LOGOUT, () => ({ ...INIT_STATE }));
  },
});

export const {
  setHasNewNotifications,
  markChatAsReadLocal,
  setActiveUserInfluencerId,
  setNewPurchase,
  setSelectedCollectionId,
} = collectionsSlice.actions;

export const selectFetchCollectionsInfo = state => state.collectionInfo.fetchCollectionsInfo;
export const selectFetchCollectionUsersInfo = state => state.collectionInfo.fetchCollectionUsersInfo;
export const selectFetchInfluencerChatsInfo = state => state.collectionInfo.fetchInfluencerChatsInfo;
export const selectFetchInfluencerChatInfo = state => state.collectionInfo.fetchInfluencerChatInfo;
export const selectFetchNotificationsInfo = state => state.collectionInfo.fetchNotificationsInfo;
export const selectUserInfluencer = state => state.collectionInfo.userInfluencer;
export const selectHasNewNotifications = state => state.collectionInfo.hasNewNotifications;
export const selectCollectionsActionsPending = state => state.collectionInfo.pending;
export const selectFetchInfluencerMassDmHistoryInfo = state => state.collectionInfo.fetchInfluencerMassDmHistoryInfo;

export default collectionsSlice.reducer;
